import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'clsx';
import { Button, Disclaimer } from '@repo/design-system-kit';
import { ConsentCheckbox, TNCLink } from '@repo/shared-kit/components';
import { IconArrowLeftOutlined, IconCheckmarkOutlined } from '@repo/monochrome-icons';
import styles from './consent-page.module.css';

export interface ConsentPageProps {
  onContinue: (hasConsent: boolean) => void;
  onBack?: () => void;
  className?: string;
  buttonStretch?: boolean;
  layout?: 'control' | 'treatment';
}

export function ConsentPage({
  onContinue,
  onBack,
  className,
  buttonStretch = true,
  layout = 'control',
}: ConsentPageProps): React.ReactElement {
  const [hasConsent, setHasConsent] = useState<boolean>(false);
  const isNativeMobile = layout === 'treatment';

  const handleConsentChange = (isChecked: boolean): void => {
    setHasConsent(isChecked);
  };

  const handleContinue = (): void => {
    onContinue(hasConsent);
  };

  const handleBack = (): void => {
    onBack?.();
  };

  return (
    <div className={cx(styles.content, className)}>
      <h1 className="heading-section mb-32">
        <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.title" />
      </h1>

      {isNativeMobile ? (
        <>
          <p className={cx('body-standard', styles.subtitle)}>
            <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.subtitle" />
          </p>

          <div className={styles.benefitsCard}>
            <ul className={styles.benefitsList}>
              <li className={styles.benefitItem}>
                <span className={styles.checkmarkIcon}>
                  <IconCheckmarkOutlined aria-hidden="true" />
                </span>
                <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.product-updates.label" />
              </li>
              <li className={styles.benefitItem}>
                <span className={styles.checkmarkIcon}>
                  <IconCheckmarkOutlined aria-hidden="true" />
                </span>
                <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.insights.label" />
              </li>
              <li className={styles.benefitItem}>
                <span className={styles.checkmarkIcon}>
                  <IconCheckmarkOutlined aria-hidden="true" />
                </span>
                <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.special-and-partner-offers.label" />
              </li>
            </ul>
          </div>

          <div className={styles.disclaimerSection}>
            <Disclaimer.Inline level="info">
              <FormattedMessage
                id="account-management-kit.sign_up.gdpr-consent.disclaimer.text"
                values={{
                  link: (
                    <TNCLink
                      className={`body-standard-link ${styles.disclaimerLink}`}
                      language="en"
                      tncKey="qonto-privacy-policy"
                    >
                      <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.disclaimer.link" />
                    </TNCLink>
                  ),
                }}
              />
            </Disclaimer.Inline>
          </div>

          <div className={styles.treatmentConsentSection}>
            <ConsentCheckbox onChange={handleConsentChange}>
              <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.receive-updates.checkbox" />
            </ConsentCheckbox>
          </div>
        </>
      ) : (
        <>
          <div className={styles.consentSection}>
            <ConsentCheckbox onChange={handleConsentChange}>
              <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.caption" />
            </ConsentCheckbox>
          </div>
        </>
      )}

      <div>
        <Button
          className="mb-16"
          data-test-consent-continue
          onClick={handleContinue}
          size="medium"
          stretch={buttonStretch}
          variant="primary"
        >
          <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.continue" />
        </Button>
      </div>

      {!isNativeMobile && (
        <div className={styles.additionalInfo}>
          <span className="caption">
            <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.footer-text" />
            <TNCLink className="caption-link" language="en" tncKey="qonto-privacy-policy">
              <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.link-text" />
            </TNCLink>
            <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.footer-end" />
          </span>
        </div>
      )}

      {onBack ? (
        <div className={styles.backButtonContainer}>
          <Button
            className={styles.backButton}
            onClick={handleBack}
            size="small"
            type="button"
            variant="tertiary"
          >
            <div className={styles.iconContainer}>
              <IconArrowLeftOutlined />
              <FormattedMessage id="account-management-kit.btn.back" />
            </div>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
